import { useMemo } from "react";
import { useFounderMetrics } from "@/hooks/useFounderMetrics";
import PremiumCard from "@/components/premium/PremiumCard";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ShieldAlert, AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";
import { Link } from "react-router-dom";

type Status = "green" | "yellow" | "red" | "gray";

function StatusDot({ s }: { s: Status }) {
  const map: Record<Status, string> = {
    green: "bg-emerald-500",
    yellow: "bg-amber-400",
    red: "bg-red-500",
    gray: "bg-muted-foreground/40",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[s]}`} />;
}

function Metric({
  label,
  value,
  status = "gray",
  hint,
}: {
  label: string;
  value: number | string | null | undefined;
  status?: Status;
  hint?: string;
}) {
  const display = value === null || value === undefined ? "—" : String(value);
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border/30 last:border-0">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-sm font-medium tabular-nums">{display}</span>
        <StatusDot s={status} />
      </div>
    </div>
  );
}

function Section({ title, children, score }: { title: string; children: React.ReactNode; score?: string }) {
  return (
    <PremiumCard className="flex flex-col gap-1">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-heading font-semibold">{title}</h2>
        {score && <span className="text-[10px] text-muted-foreground">{score}</span>}
      </div>
      {children}
    </PremiumCard>
  );
}

export default function FounderReadinessDashboard() {
  const { data, loading, error, refetch } = useFounderMetrics();

  const scores = useMemo(() => {
    if (!data) return null;
    const c = data.content;
    const a = data.activation;
    const ai = data.ai;
    const rev = data.revenue;

    const contentScore =
      c.launch_items_total && Number(c.launch_items_total) > 0
        ? Math.round((Number(c.launch_ready ?? 0) / Number(c.launch_items_total)) * 10)
        : 0;
    const activationScore = Math.min(10, Math.round((Number(a.user_progress_completed ?? 0) / 50) * 10));
    const analyticsScore = 8; // wired (Meta + GA4 + first-session)
    const aiScore =
      ai.interactions_7d != null && Number(ai.interactions_7d) > 0
        ? Math.max(0, 10 - Math.round((Number(ai.failures_7d ?? 0) / Math.max(Number(ai.interactions_7d), 1)) * 100))
        : 5;
    const revenueScore = Number(rev.paying_households ?? 0) > 0 ? 5 : 2;
    const acquisitionScore = Math.min(10, Math.round((Number(data.acquisition.households_30d ?? 0) / 10) * 10));
    const parentScore = Number(a.first_parent_insights ?? 0) > 0 ? 5 : 2;
    const teenScore = Number(a.child_profiles_total ?? 0) > 0 ? 5 : 2;

    const list: [string, number][] = [
      ["Acquisition", acquisitionScore],
      ["Activation", activationScore],
      ["Parent engagement", parentScore],
      ["Teen engagement", teenScore],
      ["Content", contentScore],
      ["Analytics", analyticsScore],
      ["AI stability", aiScore],
      ["Revenue", revenueScore],
    ];
    const overall = Math.round(list.reduce((a, [, n]) => a + n, 0) / list.length);
    return { list, overall };
  }, [data]);

  const blockers = useMemo(() => {
    const b: { msg: string; level: Status }[] = [];
    if (data) {
      if (Number(data.content.needs_review ?? 0) > 0)
        b.push({ msg: `${data.content.needs_review} launch items still need human QA review`, level: "red" });
      if (Number(data.content.missing_hook ?? 0) > 0)
        b.push({ msg: `${data.content.missing_hook} launch items missing parent insight hook`, level: "yellow" });
      if (Number(data.content.missing_minutes ?? 0) > 0)
        b.push({ msg: `${data.content.missing_minutes} launch items missing estimated minutes`, level: "yellow" });
      if (Number(data.activation.child_profiles_total ?? 0) === 0)
        b.push({ msg: "No teen profiles yet — insufficient real activation data", level: "yellow" });
      if (Number(data.revenue.paying_households ?? 0) === 0)
        b.push({ msg: "No paying households yet — revenue readiness unverified", level: "yellow" });
    }
    b.push({ msg: "MRR unavailable — requires verified Stripe price mapping (intentionally deferred)", level: "yellow" });
    b.push({ msg: "Legal/attorney review pending (see docs/legal-attorney-review-checklist-heirwise.md)", level: "yellow" });
    return b;
  }, [data]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <ShieldAlert className="w-8 h-8 text-destructive" />
        <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
        <Button onClick={refetch} variant="outline" size="sm">Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-semibold">Founder Readiness</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Generated {new Date(data.generated_at).toLocaleString()} · Admin-only command center
            </p>
          </div>
          <Button onClick={refetch} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {scores && (
          <PremiumCard variant="glow">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-heading font-semibold">Launch Readiness Score</h2>
              <span className="text-2xl font-heading font-semibold">{scores.overall}/10</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {scores.list.map(([label, n]) => (
                <div key={label} className="bg-card/50 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium tabular-nums">{n}</span>
                </div>
              ))}
            </div>
          </PremiumCard>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title="Acquisition">
            <Metric label="Households (total)" value={data.acquisition.households_total} status="green" />
            <Metric label="Households (last 7d)" value={data.acquisition.households_7d} />
            <Metric label="Households (last 30d)" value={data.acquisition.households_30d} />
            <Metric label="Waitlist / funnel leads" value={data.acquisition.funnel_leads_total} />
            <Metric label="Referred households" value={data.acquisition.referred_households_total} />
            <Metric label="Founding members" value={data.acquisition.founding_households_total} />
          </Section>

          <Section title="Activation">
            <Metric label="Teen profiles" value={data.activation.child_profiles_total} status={Number(data.activation.child_profiles_total) > 0 ? "green" : "yellow"} />
            <Metric label="Teen profiles (7d)" value={data.activation.child_profiles_7d} />
            <Metric label="Lesson attempts" value={data.activation.lesson_progress_total} />
            <Metric label="Lessons completed" value={data.activation.user_progress_completed} />
            <Metric label="Launch Path completions" value={data.activation.learner_launch_path_total} />
            <Metric label="Parent insights generated" value={data.activation.first_parent_insights} />
          </Section>

          <Section title="Engagement (rolling)">
            <Metric label="Lesson activity (7d)" value={data.engagement.lesson_activity_7d} />
            <Metric label="Lesson activity (30d)" value={data.engagement.lesson_activity_30d} />
            <Metric label="Simulation runs (total)" value={data.engagement.simulation_runs_total} />
            <Metric label="Simulation runs (7d)" value={data.engagement.sims_7d} />
            <Metric label="Module completions" value={data.engagement.module_completions_total} />
            <Metric label="Quiz attempts" value={data.engagement.quiz_attempts_total} />
          </Section>

          <Section title="Referral performance">
            <Metric label="Referral rewards granted" value={data.referral.referral_rewards_total} />
            <Metric label="Referred households" value={data.referral.referred_households_total} />
            <Metric label="Founding members" value={data.referral.founding_households_total} />
          </Section>

          <Section title="Content readiness (Launch Path)">
            <Metric label="Items total" value={data.content.launch_items_total} />
            <Metric label="Launch-ready" value={data.content.launch_ready} status={Number(data.content.launch_ready) > 0 ? "green" : "red"} />
            <Metric label="Approved" value={data.content.approved} />
            <Metric label="Needs review" value={data.content.needs_review} status={Number(data.content.needs_review) > 0 ? "yellow" : "green"} />
            <Metric label="Needs improvement" value={data.content.needs_improvement} />
            <Metric label="Avg QA score" value={data.content.avg_qa_score} hint="Set during human QA" />
            <Metric label="Missing parent insight hook" value={data.content.missing_hook} />
            <Metric label="Missing estimated minutes" value={data.content.missing_minutes} />
            <Metric label="Missing competency" value={data.content.missing_competency} />
            <div className="pt-2">
              <Link to="/admin/launch-qa">
                <Button variant="outline" size="sm" className="w-full">Review Launch Path Items →</Button>
              </Link>
            </div>
          </Section>

          <Section title="AI health">
            <Metric label="AI interactions (total)" value={data.ai.interactions_total} />
            <Metric label="AI interactions (7d)" value={data.ai.interactions_7d} />
            <Metric label="AI failures (7d)" value={data.ai.failures_7d} status={Number(data.ai.failures_7d ?? 0) === 0 ? "green" : "yellow"} hint="From ai_interaction_logs.status='error' when present" />
          </Section>

          <Section title="Revenue readiness">
            <Metric label="Active subscriptions" value={data.revenue.active_subscriptions as number} />
            <Metric label="Trialing" value={data.revenue.trialing as number} />
            <Metric label="Paying households" value={data.revenue.paying_households as number} />
            <Metric label="Canceled" value={data.revenue.canceled as number} />
            <Metric label="Billing exempt" value={data.revenue.billing_exempt as number} />
            <Metric label="MRR" value="—" hint={String(data.revenue.mrr_note)} status="gray" />
          </Section>

          <Section title="Derived rates">
            <Metric label="Teen-profiles / household" value={`${data.derived.account_to_household_pct ?? 0}%`} />
            <Metric label="Launch Path completion (est.)" value={`${data.derived.launch_path_completion_pct_estimate ?? 0}%`} hint="Rough estimate; will sharpen with real users" />
          </Section>
        </div>

        <PremiumCard>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-heading font-semibold">Open launch blockers</h2>
          </div>
          <ul className="space-y-2">
            {blockers.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                {b.level === "red" ? (
                  <MinusCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                ) : b.level === "yellow" ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                )}
                <span className="text-muted-foreground">{b.msg}</span>
              </li>
            ))}
          </ul>
        </PremiumCard>

        <p className="text-[10px] text-muted-foreground/70 text-center pt-2">
          Admin-only · No raw learner responses or private notes are displayed. All metrics are aggregated counts.
        </p>
      </div>
    </div>
  );
}
